import { Injectable, Injector, inject } from '@angular/core';
import {
    EMPTY,
    Observable,
    defer,
    forkJoin,
    merge,
    of,
    throwError,
} from 'rxjs';
import {
    catchError,
    defaultIfEmpty,
    map,
    switchMap,
    take,
} from 'rxjs/operators';
import moment from 'moment';
import {
    each as _each,
    filter as _filter,
    find as _find,
    flatMap as _flatMap,
    get as _get,
    groupBy as _groupBy,
    includes as _includes,
    isArray as _isArray,
    isEmpty as _isEmpty,
    map as _map,
    orderBy as _orderBy,
    padStart as _padStart,
    reduce as _reduce,
    toString as _toString,
    trim as _trim,
} from 'lodash';
import {
    LazadaStoreConfig,
    ShopeeStoreConfig,
    StoreConfig,
    TiktokStoreConfig,
} from 'src/app/shared/config/store-config';
import {
    InvoiceRequestService,
    LazadaOpenService,
    ShopeeOpenServiceV2,
    TiktokOpenService,
    UtilService,
} from 'src/app/shared/services';
import { InvoiceRequest } from 'src/app/shared/services/invoice-request/interfaces/api';
import {
    COLLECTOR_PLATFORMS,
    PLATFORM_AGNOSTIC,
} from 'src/app/shared/services/invoice-request/platform-map';
import { PrintSheetUtils } from 'src/app/shared/services/print-sheet/utils';
import { LazadaFormatDateTimePipe } from 'src/app/shared/pipes';
import { ReceiptItemInfo } from '../models/receipt-note-input';

// One marketplace order, reduced to what a receipt prints. The amounts follow
// the plain invoice's arithmetic per platform (combined-orders' build*Invoice
// Param): `items` carry the pre-discount unit prices, `discount` is everything
// the buyer did not pay of items + shipping, so that
// sum(items) - discount + shippingCost is what the buyer actually paid.
export interface FetchedOrder {
    platform: 'lazada' | 'shopee' | 'tiktok';
    orderNo: string;
    createdAt: moment.Moment | null;
    // When the buyer paid, where the platform reports it. A receipt is dated
    // by payment, so this wins over createdAt when present.
    paidAt: moment.Moment | null;
    paymentMethod: string;
    customer: {
        name: string;
        address: string;
    };
    items: ReceiptItemInfo[];
    discount: number;
    shippingCost: number;
    remark: string;
}

// An order together with the shop it turned out to belong to — what a lookup
// across several candidate shops has to answer, since the shop is no longer
// something the operator had to name up front.
export interface FoundOrder {
    store: StoreConfig;
    order: FetchedOrder;
}

// The customer block from the customer's own billing request (chatbot, web
// form or manual entry) — their legal name, tax id, branch and address as
// they want them on the document.
export interface BillingCustomer {
    name: string;
    address: string;
    taxId: string;
    branch: string;
}

// Fetches a single order from its platform for the receipt page. The platform
// services read their credentials from a DI token, so each call builds a
// store-scoped child injector the way combined-orders does — the page can
// then issue for any of the five stores without being provided for one.
@Injectable({
    providedIn: 'root',
})
export class PlatformOrderService {
    private injector = inject(Injector);
    private utilService = inject(UtilService);
    private lazadaFormatDateTimePipe = inject(LazadaFormatDateTimePipe);
    private invoiceRequestService = inject(InvoiceRequestService);

    fetchOrder(store: StoreConfig, orderNo: string): Observable<FetchedOrder> {
        const trimmedOrderNo = _trim(orderNo);
        if (!trimmedOrderNo) {
            return throwError(new Error('กรุณากรอกเลขที่คำสั่งซื้อ'));
        }
        switch (store.platform) {
            case 'lazada':
                return this.fetchLazadaOrder(store, trimmedOrderNo);
            case 'shopee':
                return this.fetchShopeeOrder(store, trimmedOrderNo);
            case 'tiktok':
                return this.fetchTiktokOrder(store, trimmedOrderNo);
        }
    }

    // Finds an order without being told which shop has it: every candidate is
    // asked at once and the first one that answers with an order wins —
    // `take(1)` then unsubscribes the losers, which aborts their still-open
    // requests rather than leaving them to finish unread. A shop that does not
    // have the order simply drops out; only when all of them have dropped out
    // does the lookup fail, and the report then says where it looked.
    findOrder(orderNo: string, stores: StoreConfig[]): Observable<FoundOrder> {
        const trimmedOrderNo = _trim(orderNo);
        if (!trimmedOrderNo) {
            return throwError(new Error('กรุณากรอกเลขที่คำสั่งซื้อ'));
        }
        if (_isEmpty(stores)) {
            return throwError(
                new Error(`ไม่มีร้านค้าที่จะค้นหาคำสั่งซื้อ ${trimmedOrderNo}`),
            );
        }
        // Deferred so each subscription collects its own failures instead of
        // appending to the previous run's.
        return defer(() => {
            const failures: unknown[] = [];
            return merge(
                ..._map(stores, (store: StoreConfig) =>
                    this.fetchOrder(store, trimmedOrderNo).pipe(
                        map((order: FetchedOrder): FoundOrder => ({
                            store,
                            order,
                        })),
                        catchError((error: unknown) => {
                            failures.push(error);
                            return EMPTY;
                        }),
                    ),
                ),
            ).pipe(
                take(1),
                defaultIfEmpty<FoundOrder, null>(null),
                switchMap((found: FoundOrder | null) =>
                    found
                        ? of(found)
                        : throwError(
                              this.lookupFailure(
                                  trimmedOrderNo,
                                  stores,
                                  failures,
                              ),
                          ),
                ),
            );
        });
    }

    // What to report when no candidate had the order. Asking one shop is the
    // old single-store path, so its own error is passed through untouched;
    // beyond that the operator needs to know the search covered several shops,
    // with the first error that was not a plain miss appended — an expired
    // token has to stay visible instead of reading as 'not found'.
    private lookupFailure(
        orderNo: string,
        stores: StoreConfig[],
        failures: unknown[],
    ): unknown {
        if (stores.length === 1 && failures.length === 1) {
            return failures[0];
        }
        const titles = _map(stores, (store: StoreConfig) => store.title).join(
            ', ',
        );
        const unexpected = _find(
            failures,
            (error: any) => !_get(error, 'orderNotFound'),
        );
        const message = `ไม่พบคำสั่งซื้อ ${orderNo} ใน ${titles}`;
        return new Error(
            unexpected
                ? `${message} — ${this.utilService.errorToString(unexpected)}`
                : message,
        );
    }

    // The usable billing request for the order, if the customer filed one:
    // complete or issued, not erased, and carrying both a name and an
    // address — the same all-or-nothing predicate the orders grid applies, so
    // a partial record never mixes the customer's identity with the
    // platform's. Resolves to null when there is none.
    fetchBillingCustomer(
        store: StoreConfig,
        orderNo: string,
    ): Observable<BillingCustomer | null> {
        return this.invoiceRequestService
            .list({
                storeId: store.storeId,
                orderSn: _trim(orderNo),
                limit: 20,
            })
            .pipe(
                map((response) => {
                    const platform = COLLECTOR_PLATFORMS[store.platform];
                    const candidates = _filter(
                        response.items,
                        (item: InvoiceRequest) =>
                            PlatformOrderService.isUsableBillingRequest(item) &&
                            (item.platform === platform ||
                                _includes(PLATFORM_AGNOSTIC, item.platform)),
                    );
                    const request = _orderBy(
                        candidates,
                        [
                            (item: InvoiceRequest) =>
                                item.platform === platform ? 0 : 1,
                            (item: InvoiceRequest) =>
                                item.audit?.updatedAt || '',
                        ],
                        ['asc', 'desc'],
                    )[0];
                    return request
                        ? PlatformOrderService.toBillingCustomer(request)
                        : null;
                }),
            );
    }

    private static isUsableBillingRequest(item: InvoiceRequest): boolean {
        if (
            item.erased ||
            (item.status !== 'complete' && item.status !== 'issued')
        ) {
            return false;
        }
        const invoice = item.invoice || {};
        return (
            !_isEmpty(_trim(_toString(invoice.legalName))) &&
            !_isEmpty(
                PrintSheetUtils.constructThaiFullAddress(invoice.address || {}),
            )
        );
    }

    private static toBillingCustomer(request: InvoiceRequest): BillingCustomer {
        const invoice = request.invoice || {};
        return {
            name: _trim(_toString(invoice.legalName)),
            address: PrintSheetUtils.constructThaiFullAddress(
                invoice.address || {},
            ),
            taxId: _trim(_toString(invoice.taxId)),
            branch: PlatformOrderService.formatBranch(invoice.branchCode),
        };
    }

    // The collector stores the branch as a 5-digit code ("00000" = head
    // office); printed as สำนักงานใหญ่ or the zero-padded branch number.
    // Anything non-numeric is not a branch code and prints nothing.
    private static formatBranch(branchCode: string | null | undefined): string {
        const code = _trim(_toString(branchCode));
        if (!/^[0-9]+$/.test(code)) {
            return '';
        }
        const branchNo = Number(code);
        return branchNo === 0
            ? 'สำนักงานใหญ่'
            : `สาขาที่ ${_padStart(_toString(branchNo), 5, '0')}`;
    }

    private storeInjector(
        authInfoToken: string,
        store: StoreConfig,
        serviceClasses: any[],
    ): Injector {
        return Injector.create({
            providers: [
                { provide: authInfoToken, useValue: store.authInfo },
                ...serviceClasses,
            ],
            parent: this.injector,
        });
    }

    private fetchLazadaOrder(
        store: LazadaStoreConfig,
        orderNo: string,
    ): Observable<FetchedOrder> {
        const service = this.storeInjector('lazadaAuthInfo', store, [
            LazadaOpenService,
        ]).get(LazadaOpenService);
        return service.getOrder(orderNo).pipe(
            switchMap((order: any) => {
                if (!order || _isEmpty(order)) {
                    return throwError(this.notFound(orderNo));
                }
                const orderId = _toString(order.order_id || orderNo);
                return forkJoin([of(order), service.getOrderItems([orderId])]);
            }),
            map(([order, orderItemList]: [any, any[]]): FetchedOrder => {
                const orderItemObj = _find(
                    orderItemList,
                    (entry: any) =>
                        _toString(entry.order_id) === _toString(order.order_id),
                );
                const orderItems: any[] =
                    _get(orderItemObj, 'order_items') ||
                    _get(orderItemList, '[0].order_items') ||
                    [];
                const createdAt =
                    this.lazadaFormatDateTimePipe.tryParseToMoment(
                        order.created_at,
                    );
                return {
                    platform: 'lazada',
                    orderNo: _toString(order.order_id || orderNo),
                    createdAt:
                        moment.isMoment(createdAt) && createdAt.isValid()
                            ? createdAt
                            : null,
                    paidAt: null,
                    paymentMethod: _toString(order.payment_method || ''),
                    customer: {
                        name: this.lazadaFullName(order.address_billing),
                        address: this.lazadaFullAddress(order.address_billing),
                    },
                    items: this.groupUnitItems(
                        orderItems,
                        'sku',
                        (item: any) => item.name,
                        (item: any) => item.item_price,
                        (item: any) =>
                            _includes(item.variation, 'เป็นกลาง')
                                ? ''
                                : item.variation,
                    ),
                    discount: this.utilService.localStringToNumber(
                        order.voucher || 0,
                    ),
                    shippingCost: this.utilService.localStringToNumber(
                        order.shipping_fee || 0,
                    ),
                    remark: _toString(order.remarks || ''),
                };
            }),
        );
    }

    private fetchShopeeOrder(
        store: ShopeeStoreConfig,
        orderNo: string,
    ): Observable<FetchedOrder> {
        const service = this.storeInjector('ssmAuthInfo', store, [
            ShopeeOpenServiceV2,
        ]).get(ShopeeOpenServiceV2);
        return service.getOrderDetails([orderNo]).pipe(
            map((orders: any[]): FetchedOrder => {
                const order = _find(
                    orders,
                    (entry: any) => !!entry && !!entry.order_sn,
                );
                if (!order) {
                    throw this.notFound(orderNo);
                }
                const totalItemPrice = _reduce(
                    order.item_list,
                    (accumulator: number, item: any) =>
                        accumulator +
                        this.utilService.localStringToNumber(
                            item.model_original_price,
                        ) *
                            this.utilService.localStringToNumber(
                                item.model_quantity_purchased,
                            ),
                    0,
                );
                const shippingFee = order.actual_shipping_fee_confirmed
                    ? this.utilService.localStringToNumber(
                          order.actual_shipping_fee,
                      )
                    : this.utilService.localStringToNumber(
                          order.estimated_shipping_fee,
                      );
                const netPrice = this.utilService.localStringToNumber(
                    order.total_amount,
                );
                return {
                    platform: 'shopee',
                    orderNo: _toString(order.order_sn),
                    createdAt: order.create_time
                        ? moment.unix(order.create_time)
                        : null,
                    paidAt: order.pay_time ? moment.unix(order.pay_time) : null,
                    paymentMethod: _toString(order.payment_method || ''),
                    // Shopee masks both fields in the API; the orders grid
                    // works around it with rendered images, which the receipt
                    // template cannot print. The operator overtypes them.
                    customer: {
                        name: _toString(
                            _get(order, 'recipient_address.name') || '',
                        ),
                        address: _toString(
                            _get(order, 'recipient_address.full_address') || '',
                        ),
                    },
                    items: _map(
                        order.item_list,
                        (item: any, index: number): ReceiptItemInfo => {
                            const quantity =
                                this.utilService.localStringToNumber(
                                    item.model_quantity_purchased,
                                );
                            const unitPrice =
                                this.utilService.localStringToNumber(
                                    item.model_original_price,
                                );
                            return {
                                itemNo: index + 1,
                                description:
                                    PrintSheetUtils.constructItemDescription(
                                        item.item_sku,
                                        item.item_name,
                                        quantity,
                                        item.model_name,
                                        item.model_sku,
                                    ),
                                unitPrice,
                                quantity,
                                amount: unitPrice * quantity,
                            };
                        },
                    ),
                    discount: totalItemPrice + shippingFee - netPrice,
                    shippingCost: shippingFee,
                    remark: _toString(order.message_to_seller || ''),
                };
            }),
        );
    }

    private fetchTiktokOrder(
        store: TiktokStoreConfig,
        orderNo: string,
    ): Observable<FetchedOrder> {
        const service = this.storeInjector('tiktokAuthInfo', store, [
            TiktokOpenService,
        ]).get(TiktokOpenService);
        return service.getOrderDetail([orderNo]).pipe(
            map((responses: any[]): FetchedOrder => {
                // The delegate hands back TikTok's `data` per chunk, i.e.
                // `{ orders: [...] }`; tolerate a pre-flattened list too.
                const orders: any[] = _flatMap(responses, (response: any) =>
                    _isArray(response)
                        ? response
                        : _get(response, 'orders') || [],
                );
                const order = _find(orders, (entry: any) => !!entry?.id);
                if (!order) {
                    throw this.notFound(orderNo);
                }
                // Pre-discount amounts, as on the invoice: shipping_fee is what
                // the buyer paid after platform subsidies (often 0), while the
                // document shows the original fee with the subsidy on the
                // discount line.
                const shippingFee = this.utilService.localStringToNumber(
                    _get(order, 'payment.original_shipping_fee') || 0,
                );
                const items = this.groupUnitItems(
                    order.line_items || [],
                    'seller_sku',
                    (item: any) => item.product_name,
                    (item: any) => item.original_price,
                    (item: any) => item.sku_name,
                );
                const totalItemPrice =
                    this.utilService.localStringToNumber(
                        _get(order, 'payment.original_total_product_price') ||
                            0,
                    ) ||
                    _reduce(
                        items,
                        (accumulator, item) => accumulator + item.amount,
                        0,
                    );
                const netPrice = this.utilService.localStringToNumber(
                    _get(order, 'payment.total_amount') || 0,
                );
                return {
                    platform: 'tiktok',
                    orderNo: _toString(order.id),
                    createdAt: order.create_time
                        ? moment.unix(order.create_time)
                        : null,
                    paidAt: order.paid_time
                        ? moment.unix(order.paid_time)
                        : null,
                    paymentMethod: _toString(order.payment_method_name || ''),
                    customer: {
                        name: this.shortenAsteriskMask(
                            _toString(
                                _get(order, 'recipient_address.name') || '',
                            ),
                        ),
                        address: this.shortenAsteriskMask(
                            _toString(
                                _get(order, 'recipient_address.full_address') ||
                                    '',
                            ),
                        ),
                    },
                    items,
                    discount: totalItemPrice + shippingFee - netPrice,
                    shippingCost: shippingFee,
                    remark: _toString(order.buyer_message || ''),
                };
            }),
        );
    }

    // Tagged so a fan-out lookup can tell a shop that simply does not have
    // the order from one that could not answer at all.
    private notFound(orderNo: string): Error {
        return Object.assign(new Error(`ไม่พบคำสั่งซื้อ ${orderNo}`), {
            orderNotFound: true,
        });
    }

    private lazadaFullName(addressObj: any): string {
        return `${_get(addressObj, 'first_name') || ''} ${
            _get(addressObj, 'last_name') || ''
        }`.trim();
    }

    private lazadaFullAddress(addressObj: any): string {
        const addressFields: string[] = [
            'address1',
            'address2',
            'address3',
            'address4',
            'address5',
            'country',
        ];
        let fullAddressString = '';
        _each(addressFields, (addressField) => {
            const address = _trim(_get(addressObj, addressField));
            if (!_isEmpty(address)) {
                fullAddressString = _isEmpty(fullAddressString)
                    ? address
                    : `${fullAddressString}, ${address}`;
            }
        });
        return fullAddressString;
    }

    // Groups per-unit item entries (Lazada order_items / TikTok line_items
    // contain one entry per unit) into one row per SKU.
    private groupUnitItems(
        rawItems: any[],
        skuField: string,
        getName: (item: any) => string,
        getPrice: (item: any) => any,
        getVariation: (item: any) => string,
    ): ReceiptItemInfo[] {
        let itemNo = 0;
        const groupedItems = _groupBy(rawItems, skuField);
        return _map(groupedItems, (items: any[], sku: string) => {
            const candidateItem = items[0];
            const quantity = items.length;
            const unitPrice = this.utilService.localStringToNumber(
                getPrice(candidateItem),
            );
            return {
                itemNo: ++itemNo,
                description: PrintSheetUtils.constructItemDescription(
                    sku,
                    getName(candidateItem),
                    quantity,
                    getVariation(candidateItem),
                ),
                quantity,
                unitPrice,
                amount: unitPrice * quantity,
            };
        });
    }

    // TikTok masks recipient name/address with long runs of asterisks;
    // collapse any run of 3+ asterisks down to exactly 3 to keep it tidy.
    private shortenAsteriskMask(value: string): string {
        return value.replace(/\*{3,}/g, '***');
    }
}
